import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";

import ModalUsuarios from "../componentes/modalusuario";
import ModalEditarUsuario from "../componentes/editarusuario";

export default function TablaUsuariosPremium() {
  const [usuario, setUsuario] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // 🔹 Obtener usuario desde localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem("usuario");
    if (storedUser) {
      try {
        setUsuario(JSON.parse(storedUser));
      } catch (e) {
        console.error("Error al parsear usuario:", e);
      }
    }
  }, []);

  // 🔹 Obtener usuarios
  useEffect(() => {
    if (usuario) getUsuarios(usuario.empresa_idempresa);
  }, [usuario]);

  const getUsuarios = async (empresaId) => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.post(process.env.REACT_APP_API_USUARIOS, {
        empresa_id: empresaId
      });
      setUsuarios(res.data);
    } catch (err) {
      console.error(err);
      setError("Error al obtener usuarios");
    } finally {
      setLoading(false);
    }
  };

  const deleteUsuario = async (idUsuarios) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_API_USUARIOS_ELIMINAR}?id=${idUsuarios}`
      );
      getUsuarios(usuario.empresa_idempresa);
    } catch (err) {
      console.error(err);
      setError("Error al eliminar usuario");
    }
  };

  const filteredData = useMemo(() => {
    return usuarios.filter((u) =>
      [u.nombre, u.apellido, u.rut, u.empresa]
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [usuarios, search]);

  if (!usuario)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ p: 3 }}>
      {/* HEADER */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administración y gestión de usuarios registrados
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            placeholder="Buscar usuario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <ModalUsuarios />
        </Stack>
      </Stack>

      {/* ERROR */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* LOADING */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ background: "linear-gradient(90deg, #1976d2, #42a5f5)", color: "#fff" }}>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>ID</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Nombre</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Apellido</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>RUT</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Empresa</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No se encontraron usuarios
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((item) => (
                  <TableRow
                    key={item.idUsuarios}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(25, 118, 210, 0.08)" }
                    }}
                  >
                    <TableCell>{item.idUsuarios}</TableCell>
                    <TableCell>{item.nombre}</TableCell>
                    <TableCell>{item.apellido}</TableCell>
                    <TableCell>{item.rut}</TableCell>
                    <TableCell>{item.empresa}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Editar">
                          <ModalEditarUsuario usuarios={item}>
                            <IconButton color="primary">
                              <EditIcon />
                            </IconButton>
                          </ModalEditarUsuario>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                          <IconButton
                            color="error"
                            onClick={() => deleteUsuario(item.idUsuarios)}
                          >
                            <DeleteForeverIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}